import React, { useEffect, useRef, useState } from "react";
import { Btn, CheckoutTileDetail, FormInput, FormTextarea } from "../components";
import { useCore, useLogin } from "../hooks";
import { useNavigate, useParams } from "react-router";
import { Checkout } from "depoto-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useGQL } from "../utils/GQLContext.ts";
import { getDateStringFromUTCString, Schema } from "../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const CheckoutOpen: React.FC = () => {
  const params = useParams()
  const gql = useGQL()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { core, checkouts, setCheckouts, setSelectedCheckoutId } = useCore()
  const { user } = useLogin()
  const [checkout, setCheckout] = useState<Checkout>(null)
  const [realOpeningAmount, setRealOpeningAmount] = useState<number>(0)
  const [note, setNote] = useState<string>('')
  const [isOpening, setIsOpening] = useState<boolean>(false)
  const isOpeningRef = useRef<boolean>(false) // state is not updated synchronously, ref guards fast double click

  useEffect(() => {
    if (params?.id) {
      loadCheckout(params.id)
    }
  }, [params])

  const fetchCheckout = async (checkoutId: number) => {
    const refreshedCheckouts = await core.services.checkout.getList({ filters: {} }, Schema.checkout.detail).then(r => r.items || [])
    return refreshedCheckouts.find(c => c.id === checkoutId)
  }

  // dashboard checks open state from store, so it has to be up to date before navigating there
  const selectAndGoToDashboard = (c: Checkout) => {
    setCheckouts(checkouts.map(ch => ch.id === c.id ? c : ch))
    setSelectedCheckoutId(c.id)
    navigate('/dashboard')
  }

  const loadCheckout = async (checkoutId: string) => {
    const c = await fetchCheckout(Number(checkoutId))
    if (!c) return
    const isAlreadyOpen = c.currentClosing?.dateOpen && !c.currentClosing?.dateClosed
    if (isAlreadyOpen) {
      selectAndGoToDashboard(c)
      return
    }
    setCheckout(c)
    setRealOpeningAmount(c.amount)
  }

  const { mutate: createCheckoutClosing } = useMutation({
    mutationKey: ['createCheckoutClosing'],
    mutationFn: () =>
      // mutationFn: (data: ValueTypes['createCheckoutClosing'][]) =>
      toast.promise(
        gql('mutation')({
          createCheckoutClosing: [
            {
              checkout: checkout.id,
              dateOpen: getDateStringFromUTCString(),
              amountOpen: checkout.amount.toFixed(2),
              amountRealOpen: realOpeningAmount.toFixed(2),
              userOpen: user.id,
              noteOpen: note,
            },
            {
              errors: true,
            },
          ],
        }),
        {
          pending: 'Otevírám pokladnu...',
          error: 'Něco se pokazilo',
          success: 'Pokladna otevřena',
        },
      ),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['checkouts', 'products'] })
      const c = await fetchCheckout(checkout.id)
      selectAndGoToDashboard(c || checkout)
    },
    onError: () => {
      isOpeningRef.current = false
      setIsOpening(false)
    },
  })

  const backToCheckoutSelect = () => {
    navigate('/checkout-select')
  }

  const openCheckout = async () => {
    if (isOpeningRef.current) return
    isOpeningRef.current = true
    setIsOpening(true)
    // checkout could have been opened on another device since this page was loaded
    let c: Checkout
    try {
      c = await fetchCheckout(checkout.id)
    } catch {
      c = undefined
    }
    if (!c) {
      toast.error('Nepodařilo se ověřit stav pokladny')
      isOpeningRef.current = false
      setIsOpening(false)
      return
    }
    const isAlreadyOpen = c.currentClosing?.dateOpen && !c.currentClosing?.dateClosed
    if (isAlreadyOpen) {
      toast.info(`Pokladna ${c.name} už je otevřená`)
      selectAndGoToDashboard(c)
      return
    }
    createCheckoutClosing()
  }

  return checkout?.id > 0 ? (
    <div className="w-full h-body  max-w-xl m-auto flex flex-col items-center justify-center">
      <h1 className="text-center font-extrabold text-3xl mb-6 uppercase">Uzavřená pokladna</h1>
      <div className="border border-base-300 rounded-md bg-white w-full">
        <div className="flex flex-row justify-center mb-4 border-b border-base-300 py-4 px-4">
          <div className="font-semibold text-base-400 text-2xl flex flex-col items-center">
            <FontAwesomeIcon icon={['far', 'cash-register']} className="text-primary text-2xl mb-2" />
            {checkout.name}
          </div>
        </div>
        <div className="border-b border-base-300">
          <CheckoutTileDetail checkout={checkout} />
        </div>
        <div className="p-4">
          <FormInput label={'Aktuální stav kasy'} value={`${realOpeningAmount}`} isDisabled={true} className="flex !flex-row items-center justify-between" onChange={val => setRealOpeningAmount(Number(val))} />
          <FormTextarea textAreaclassName="w-full" label={'Poznámka'} value={note} onChange={val => setNote(val)} />
        </div>
        <div className="flex flex-row justify-between p-4">
          <Btn className="btn-primary btn-soft" children={'Změnit pokladnu'} icon={'fa-sync'} onClick={backToCheckoutSelect} />
          <Btn className="btn-primary" children={'Otevřít pokladnu'} icon={'fa-upload'} isDisabled={isOpening} onClick={openCheckout} />
        </div>
      </div>
    </div>
  ) : null
}
